import { computed, ref } from 'vue'
import { getApiBase, getAuthToken } from '@/store/api'

const PREF_KEY = 'obra_push_enabled'

// Web Notifications API (basic — in-browser only)
const hasNotifAPI = typeof window !== 'undefined' && 'Notification' in window

// Web Push API (real background push via service worker)
const hasWebPush = hasNotifAPI && 'serviceWorker' in navigator && 'PushManager' in window

// Platform detection
const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
const isIOS = /iphone|ipad|ipod/i.test(ua)
const isAndroid = /android/i.test(ua)
const isMac = /macintosh|mac os x/i.test(ua) && !isIOS
const isWindows = /windows/i.test(ua)
// PWA detection: standalone mode means installed to home screen
const isPWA = typeof window !== 'undefined' && (
  window.matchMedia('(display-mode: standalone)').matches ||
  ('standalone' in window.navigator && (window.navigator as Record<string, unknown>).standalone === true)
)

const permission = ref<NotificationPermission>(hasNotifAPI ? Notification.permission : 'denied')

// true = user opted in AND permission granted
const enabled = ref<boolean>(
  hasNotifAPI && localStorage.getItem(PREF_KEY) === 'true' && Notification.permission === 'granted'
)

// true when we have an active push subscription registered with the backend
const webPushActive = ref(false)

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
}

async function getVapidKey(): Promise<string | null> {
  try {
    const res = await fetch(`${getApiBase()}/push/vapid-key`)
    if (!res.ok) return null
    const data = await res.json()
    return data.publicKey || null
  } catch (_) { return null }
}

async function registerWebPush(): Promise<boolean> {
  if (!hasWebPush) return false
  try {
    const vapidKey = await getVapidKey()
    if (!vapidKey) return false
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    })
    const res = await fetch(`${getApiBase()}/push/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
      body: JSON.stringify(sub.toJSON()),
    })
    if (!res.ok) return false
    webPushActive.value = true
    return true
  } catch (_) { return false }
}

async function unregisterWebPush(): Promise<void> {
  if (!hasWebPush) return
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (!sub) { webPushActive.value = false; return }
    await fetch(`${getApiBase()}/push/subscribe`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthToken()}` },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    })
    await sub.unsubscribe()
    webPushActive.value = false
  } catch (_) { /* non-blocking */ }
}

async function checkWebPushActive(): Promise<void> {
  if (!hasWebPush) return
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    webPushActive.value = !!sub
  } catch (_) { webPushActive.value = false }
}

// Call once on init to sync webPushActive state
if (hasWebPush) checkWebPushActive()

async function requestPermission(): Promise<boolean> {
  if (!hasNotifAPI) return false
  const result = await Notification.requestPermission()
  permission.value = result
  if (result === 'granted') {
    enabled.value = true
    localStorage.setItem(PREF_KEY, 'true')
    await registerWebPush()
  }
  return result === 'granted'
}

async function setEnabled(val: boolean): Promise<void> {
  if (val && permission.value !== 'granted') return
  enabled.value = val
  localStorage.setItem(PREF_KEY, val ? 'true' : 'false')
  if (val) {
    await registerWebPush()
  } else {
    await unregisterWebPush()
  }
}

// Fallback: show in-browser notification when app is open and web push isn't active
function showLocalNotification(title: string, body: string, icon?: string) {
  if (!hasNotifAPI || permission.value !== 'granted' || !enabled.value) return
  new Notification(title, { body, icon: icon || '/favicon.ico', badge: '/favicon.ico', tag: title })
}

export function usePushNotifications() {
  return {
    isSupported: hasNotifAPI,
    isWebPushSupported: hasWebPush,
    isIOS,
    isAndroid,
    isMac,
    isWindows,
    isPWA,
    permission: computed(() => permission.value),
    enabled: computed(() => enabled.value),
    webPushActive: computed(() => webPushActive.value),
    requestPermission,
    setEnabled,
    showLocalNotification,
    checkWebPushActive,
  }
}
